import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PermissionGate from '../../../components/common/PermissionGate';
import hrService from '../../../api/services/hrService';
import locationService from '../../../api/services/locationService';
import departmentRoleService from '../../../api/services/departmentRoleService';
import {
  ArrowLeft,
  Save,
  X,
  ArrowRight,
  User,
  Building,
  MapPin,
  CheckCircle
} from 'lucide-react';

const TransferRoleChange = () => {
  const navigate = useNavigate();
  const { employeeId } = useParams();

  // State management - ALL HOOKS MUST BE AT THE TOP
  const [employee, setEmployee] = useState(null);
  const [originalEmployeeData, setOriginalEmployeeData] = useState(null); // Store original employee data for fallback values
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    newDepartment: '',
    newLocation: '',
    effectiveDate: '',
    reason: '',
    comments: ''
  });


  // Mock employee data
  const mockEmployee = {
    id: employeeId || 'EMP001',
    employeeId: 'EMP001',
    name: 'John Smith',
    currentDepartment: 'Sales',
    currentRole: 'Sales Executive',
    currentLocation: 'Mumbai',
    currentManager: 'Sarah Johnson',
    status: 'Active'
  };

  // Define loadData function first
  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {


      // Load employee data
      const employeeResult = await hrService.getEmployeeById(employeeId);
      if (employeeResult.success) {
        const emp = employeeResult.data;

        // Store original employee data for fallback values
        setOriginalEmployeeData(emp);

        const employeeData = {
          id: emp._id,
          employeeId: emp.employeeId,
          name: `${emp.personalInfo?.firstName || ''} ${emp.personalInfo?.lastName || ''}`.trim(),
          email: emp.contactInfo?.personalEmail || emp.user?.email || '',
          phone: emp.contactInfo?.personalPhone || emp.user?.phone || '',
          currentDepartment: emp.employmentInfo?.department || '',
          currentRole: emp.employmentInfo?.position || '',
          currentLocation: (typeof emp.employmentInfo?.workLocation === 'object' ? emp.employmentInfo?.workLocation?.name : emp.employmentInfo?.workLocation) || '',
          currentManager: emp.employmentInfo?.reportingManager ?
            (emp.employmentInfo.reportingManager.fullName ||
              `${emp.employmentInfo.reportingManager.personalInfo?.firstName || ''} ${emp.employmentInfo.reportingManager.personalInfo?.lastName || ''}`.trim()) :
            'Not Assigned',
          status: emp.employmentInfo?.status === 'active' ? 'Active' :
            emp.employmentInfo?.status === 'inactive' ? 'Inactive' :
              emp.employmentInfo?.status === 'transferred' ? 'Transferred' : 'On Leave'
        };
        setEmployee(employeeData);

        // Set form data with blank values (as per user requirement)
        setFormData(prev => ({
          ...prev,
          newDepartment: '',
          newLocation: ''
        }));
      } else {
        setError(employeeResult.error || 'Employee not found');
        setEmployee(mockEmployee);
      }

    } catch (err) {

      setError('Failed to load employee data. Please try again.');
      setEmployee(mockEmployee);
    } finally {
      setLoading(false);
    }
  };

  // Load data
  useEffect(() => {
    loadData();
    loadDepartments();
    loadLocations();
  }, [employeeId]);

  // Load departments data
  const loadDepartments = async () => {
    try {

      const result = await departmentRoleService.getDepartments({ isActive: 'true' });


      let departmentsData = [];

      if (result?.responseCode === 200 && Array.isArray(result.data)) {
        departmentsData = result.data;
      } else if (Array.isArray(result)) {
        departmentsData = result;
      } else if (result?.data && Array.isArray(result.data)) {
        departmentsData = result.data;
      } else if (result?.success && Array.isArray(result.data)) {
        departmentsData = result.data;
      }

      const activeDepartments = departmentsData.filter(
        (dept) => dept && dept.isActive !== false && dept.status !== 'Inactive'
      );

      setDepartments(activeDepartments);

    } catch (error) {

      setDepartments([]);
    }
  };


  // Load locations data
  const loadLocations = async () => {
    try {

      const result = await locationService.getActiveLocations();

      if (result.success) {

        // Extract location names like JobPosts does
        const locationNames = result.data.map(loc => {
          if (typeof loc === 'string') return loc;
          if (loc && typeof loc === 'object') {
            if (loc.value) return loc.value;
            return loc.name || loc.label || loc.value || String(loc);
          }
          return String(loc);
        });
        setLocations(locationNames);

      } else {

        // Fallback to hardcoded locations
        setLocations(['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Hyderabad', 'Pune']);
      }
    } catch (error) {

      // Fallback to hardcoded locations
      setLocations(['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Hyderabad', 'Pune']);
    }
  };

  // If no employeeId is provided (accessed from sidebar), use dummy data
  const currentEmployee = employeeId ? employee : mockEmployee;

  // Handle key press for text inputs (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Text fields: only allow alphanumeric, spaces, and commas
    if (name === 'comments') {
      processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 1000);
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue || value
    }));
  };

  // Validate form
  const validateForm = () => {
    if (!formData.effectiveDate) {
      alert('Effective date is required');
      return false;
    }
    if (!formData.reason.trim()) {
      alert('Reason is required');
      return false;
    }
    if (formData.comments && formData.comments.length > 1000) {
      alert('Comments must be ≤ 1000 characters');
      return false;
    }
    if (formData.comments && !/^[a-zA-Z0-9\s,]+$/.test(formData.comments)) {
      alert('Comments can only contain alphanumeric characters, spaces, and commas');
      return false;
    }
    if (new Date(formData.effectiveDate) <= new Date()) {
      alert('Effective date must be in the future');
      return false;
    }
    return true;
  };

  // Check if at least New Department or New Location has changed
  // Button is enabled only if at least one of these fields has a value that differs from current
  const hasDepartmentOrLocationChange =
    (formData.newDepartment && formData.newDepartment.trim() !== '' && formData.newDepartment !== currentEmployee.currentDepartment) ||
    (formData.newLocation && formData.newLocation.trim() !== '' && formData.newLocation !== currentEmployee.currentLocation);

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Check if there are any changes
    const hasChanges =
      formData.newDepartment !== currentEmployee.currentDepartment ||
      formData.newLocation !== currentEmployee.currentLocation;

    if (!hasChanges) {
      alert('No changes detected. Please select different values.');
      return;
    }

    setShowConfirmModal(true);
  };

  // Confirm transfer
  const confirmTransfer = async () => {
    setIsSubmitting(true);

    try {


      // Get original values from stored employee data
      const originalDepartment = originalEmployeeData?.employmentInfo?.department || currentEmployee.currentDepartment || '';
      const originalLocation = (typeof originalEmployeeData?.employmentInfo?.workLocation === 'object' ? originalEmployeeData.employmentInfo.workLocation.name : originalEmployeeData?.employmentInfo?.workLocation) || currentEmployee.currentLocation || '';

      // Prepare transfer data - use original values if new values are not selected
      const transferData = {
        newDepartment: formData.newDepartment || originalDepartment,
        newLocation: formData.newLocation || originalLocation,
        effectiveDate: formData.effectiveDate,
        reason: formData.reason,
        comments: formData.comments || ''
      };


      // Call the actual API
      const result = await hrService.transferEmployee(employeeId, transferData);

      if (result.success) {


        // Reload employee data from database
        await loadData();

        setShowConfirmModal(false);
        setShowSuccessBanner(true);

        // Reset form
        setFormData({
          newDepartment: '',
          newLocation: '',
          effectiveDate: '',
          reason: '',
          comments: ''
        });
      } else {
        alert(`Failed to transfer employee: ${result.error}`);
      }

      // Hide success banner after 5 seconds
      setTimeout(() => {
        setShowSuccessBanner(false);
      }, 5000);

    } catch (err) {
      alert('Failed to process transfer. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };


  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-agrosaf-green"></div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Employee Not Found</h3>
        <p className="text-gray-600">The requested employee could not be found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 min-w-0 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-4 flex-1 min-w-0">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors flex-shrink-0"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            <span className="hidden sm:inline">Back</span>
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">Transfer</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1 break-words">Reassign employee department and location</p>
          </div>
        </div>
      </div>

      {/* Success Banner */}
      {showSuccessBanner && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start sm:items-center">
            <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5 sm:mt-0" />
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-medium text-green-800 break-words">Transfer Request Submitted</h3>
              <p className="text-sm text-green-600 break-words">The transfer request has been submitted and is pending approval.</p>
            </div>
          </div>
        </div>
      )}

      {/* Current Assignment Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <User className="h-5 w-5 mr-2 text-agrosaf-green flex-shrink-0" />
          Current Assignment
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700">Employee</label>
            <p className="mt-1 text-sm text-gray-900 break-words">{currentEmployee.name}</p>
            <p className="text-xs text-gray-500 break-words">ID: {currentEmployee.employeeId}</p>
          </div>
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700">Department</label>
            <p className="mt-1 text-sm text-gray-900 flex items-start sm:items-center min-w-0">
              <Building className="h-4 w-4 mr-1 flex-shrink-0 mt-0.5 sm:mt-0" />
              <span className="break-words min-w-0">{currentEmployee.currentDepartment}</span>
            </p>
          </div>
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700">Location</label>
            <p className="mt-1 text-sm text-gray-900 flex items-start sm:items-center min-w-0">
              <MapPin className="h-4 w-4 mr-1 flex-shrink-0 mt-0.5 sm:mt-0" />
              <span className="break-words min-w-0">{currentEmployee.currentLocation}</span>
            </p>
          </div>
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700">Manager</label>
            <p className="mt-1 text-sm text-gray-900 break-words">{currentEmployee.currentManager}</p>
          </div>
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 ${currentEmployee.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
              {currentEmployee.status}
            </span>
          </div>
        </div>
      </div>

      {/* Transfer Form */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <ArrowRight className="h-5 w-5 mr-2 text-agrosaf-green flex-shrink-0" />
          <span className="break-words">New Assignment</span>
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Department
              </label>
              <select
                name="newDepartment"
                value={formData.newDepartment}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
              >
                <option value="">Select department (optional)</option>
                {departments.map(dept => (
                  <option key={dept._id || dept.id || dept.name} value={dept.name}>
                    {dept.name} {dept.employeeCount ? `(${dept.employeeCount} employees)` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Location
              </label>
              <select
                name="newLocation"
                value={formData.newLocation}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
              >
                <option value="">Select location (optional)</option>
                {locations.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Effective Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="effectiveDate"
                value={formData.effectiveDate}
                onChange={handleInputChange}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason <span className="text-red-500">*</span>
              </label>
              <select
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                required
              >
                <option value="">Select reason</option>
                <option value="Promotion">Promotion</option>
                <option value="Department Transfer">Department Transfer</option>
                <option value="Location Change">Location Change</option>
                <option value="Role Enhancement">Role Enhancement</option>
                <option value="Organizational Restructure">Organizational Restructure</option>
                <option value="Performance Improvement">Performance Improvement</option>
                <option value="Personal Request">Personal Request</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Comments
            </label>
            <textarea
              name="comments"
              value={formData.comments}
              onChange={handleInputChange}
              onKeyPress={handleTextKeyPress}
              maxLength={1000}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
              placeholder="Any additional details or comments..."
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.comments.length}/1000 characters
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 sm:gap-4">
            <button
              type="button"
              onClick={() => navigate(`/employee_profile/${employeeId}`)}
              className="px-4 sm:px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors w-full sm:w-auto"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!hasDepartmentOrLocationChange}
              className="px-4 sm:px-6 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-agrosaf-green w-full sm:w-auto"
            >
              <Save className="h-4 w-4 mr-2" />
              <span className="break-words">Submit Transfer Request</span>
            </button>
          </div>
        </form>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 break-words">Confirm Transfer</h3>

            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2 break-words">Transfer Summary</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="min-w-0">
                    <h5 className="text-sm font-medium text-gray-700 mb-1">From</h5>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p className="break-words">Department: {currentEmployee.currentDepartment}</p>
                      <p className="break-words">Location: {currentEmployee.currentLocation}</p>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <h5 className="text-sm font-medium text-gray-700 mb-1">To</h5>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p className="break-words">Department: {formData.newDepartment || currentEmployee.currentDepartment}</p>
                      <p className="break-words">Location: {formData.newLocation || currentEmployee.currentLocation}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-sm text-gray-600 break-words">
                    <span className="font-medium">Effective Date:</span> {formData.effectiveDate}
                  </p>
                  <p className="text-sm text-gray-600 break-words">
                    <span className="font-medium">Reason:</span> {formData.reason}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 sm:gap-4">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors w-full sm:w-auto"
              >
                Cancel
              </button>
              <PermissionGate permission="employee_management" action="update">
                <button
                  onClick={confirmTransfer}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center w-full sm:w-auto"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Confirm Transfer
                    </>
                  )}
                </button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransferRoleChange;
